import { apiClient } from './api.client';
import { DeviceInput, OutputDevice, ManualCommand } from './event-actions.service';
const base = '/camera-security/sensor-devices';
export interface DeviceRegistration {
  name: string; category: string; connection_type: 'http_webhook' | 'hikvision' | 'manual'; enabled: boolean;
  manufacturer: string; model: string; external_id: string; zone: string; notes: string; stale_after_seconds: number;
  output: DeviceInput | null;
}
export interface RegistryDevice extends Omit<OutputDevice, 'kind'>, Omit<DeviceRegistration, 'output'> {
  kind: OutputDevice['kind'] | 'sensor'; location_id: number; health: string; token_configured: boolean;
  control_supported: boolean; last_seen_at?: string; last_occurred_at?: string;
  last_state?: { code: string; value: unknown; metrics: Record<string, unknown>; severity: string };
  ingest_token?: string;
}
export interface DeviceStatus {
  schema_ready: boolean; can_manage: boolean;
  categories: { value: string; label: string; group: string }[];
  connections: { value: string; label: string }[];
}
export interface SensorEvent {
  id: number; external_event_id: string; event_type: string; code: string; severity: string; value: unknown;
  metrics: Record<string, unknown>; message: string; snapshot_url?: string; occurred_at: string; received_at: string;
}
export interface Page<T> { items: T[]; total: number; page: number; size: number; }
export interface SensorRule {
  id?: string; name: string; sensor_id: string; device_id: string; enabled: boolean; event_code: string;
  event_type: 'alarm' | 'state' | 'measurement'; operator: 'any' | 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
  field: string; value: unknown; command: ManualCommand; review_required: boolean; cooldown_seconds: number;
  max_age_seconds: number; sensor_name?: string; device_name?: string;
}
export const sensorDevicesService = {
  status: (signal?: AbortSignal) => apiClient.get<DeviceStatus>(`${base}/status`, undefined, { signal }),
  list: (params: Record<string, any>, signal?: AbortSignal) => apiClient.get<Page<RegistryDevice>>(`${base}/devices`, params, { signal }),
  get: (id: string, signal?: AbortSignal) => apiClient.get<RegistryDevice>(`${base}/devices/${id}`, undefined, { signal }),
  save: (location: number, data: DeviceRegistration, id?: string) => id
    ? apiClient.put<RegistryDevice>(`${base}/devices/${id}`, data)
    : apiClient.post<RegistryDevice>(`${base}/locations/${location}/devices`, data),
  rotateToken: (id: string) => apiClient.post<{ ingest_token: string }>(`${base}/devices/${id}/token`),
  history: (id: string, params: Record<string, any>, signal?: AbortSignal) => apiClient.get<Page<SensorEvent>>(`${base}/devices/${id}/events`, params, { signal }),
  rules: (location: number, signal?: AbortSignal) => apiClient.get<{ items: SensorRule[] }>(`${base}/locations/${location}/rules`, undefined, { signal }),
  saveRule: (location: number, data: SensorRule) => {
    const { id, sensor_name, device_name, ...input } = data;
    return id ? apiClient.put<SensorRule>(`${base}/rules/${id}`, input) : apiClient.post<SensorRule>(`${base}/locations/${location}/rules`, input);
  },
  deleteRule: (id: string) => apiClient.delete(`${base}/rules/${id}`),
};
export const deviceHealthLabels: Record<string, string> = {
  online: 'Receiving data', offline: 'No recent data', awaiting_data: 'Awaiting first event', registered: 'Inventory only',
  verified: 'Capabilities checked', unchecked: 'Needs capability check', error: 'Connection error', disabled: 'Disabled',
};
export const deviceTime = (value?: string) => value ? new Date(/[zZ]$|[+-]\d\d:\d\d$/.test(value) ? value : value + 'Z').toLocaleString() : 'Not received yet';
